/**
 * WhyWeAsk.tsx — the "Why we ask" link under a question, and the plain
 * explanation it opens (CTO, 2026-09-24).
 *
 * Owner repo: @thegpservice/gpsclinics-portal.
 *
 * WHAT IT SHOWS. The question's own `whyWeAsk` text — clinic-service question set
 * v6, server-owned copy that rides the question set's clinical sign-off and is
 * versioned with it — split into the paragraphs its author wrote. Nothing else.
 *
 * ⚠️ NO NAME, NO PHOTO, NO BYLINE, NO "FROM OUR CLINICAL TEAM". The CTO: "Don't
 * add a person's name or anything similar … Just as an information piece." It
 * is also the estate's recorded precedent: clinical sign-off is recorded by
 * role and date, never by a person on a patient screen, because the person
 * changes while the obligation does not (prices-service medication-safety.ts).
 * A byline would also claim an authorship the text does not carry.
 *
 * ABSENT → NOTHING. No `whyWeAsk` (every set before v6, so every in-flight v5
 * assessment), or an empty/whitespace one, renders no link at all. The portal
 * never writes an explanation of its own: "renders whatever arrives" is the
 * rule for question content (QuestionCard's header), and an explanation not in
 * the question set would be copy no clinician signed and no version records.
 *
 * ACCESSIBILITY. A real `<button>` (keyboard, and announced as something that
 * opens a dialog), a 44px-tall hit area, and underline + icon so it is never
 * signalled by colour alone. The dialog is the shared `DialogShell` —
 * `role="dialog"`, `aria-modal`, focus moved in on open — and focus comes back
 * to THIS button on close.
 *
 * ⚠️ TAB STAYS INSIDE IT (`trapFocus`, review 2026-09-25). The dialog renders in
 * the question's header, so the next Tab stop after its "Close" is the FIRST
 * ANSWER, behind the scrim. A single-select answer, and a multi-select's
 * exclusive "None", submits on one key, so Tab then Enter recorded a clinical
 * answer (q3 sex at birth, q4 ethnicity, which moves the BMI threshold) that
 * the patient could not see. DialogShell has no trap by default; this dialog
 * turns it on, and so does the call-back dialog it hands off to. DialogShell returns focus to whatever was focused
 * when it opened, but Safari does not focus a button on click, so that can be
 * the page body; the effect below puts it back on the link explicitly, after
 * DialogShell's own cleanup has run.
 *
 * THE DIALOG'S LOOK (CTO, 2026-09-24: "beautify it a bit"). Journey-scoped and
 * calm, like the rest of the new journey: a soft green info badge beside a
 * Fraunces title, the paragraphs at a ~60ch measure, a hairline, the call-back
 * row, then a "Close" pill. All of it is `gps-jux-why-*` CSS (tokens.css,
 * "journey-ux: Why we ask — the dialog"), reached through DialogShell's
 * additive `className` — every other dialog renders byte-identical. The badge
 * is an aria-hidden "i" glyph: decoration, not a person — the no-photo rule is
 * about a face or a name, and the spec pins that the dialog holds no image.
 *
 * THE CALL-BACK ROW (CTO, 2026-09-24: "if they have any concerns, we can reroute
 * them to that option … show the same thing inside this modal pop-up"). The
 * page offer's own words (`CALLBACK_OFFER_LEAD` + "Request a free call back").
 * Pressing it CLOSES this dialog and OPENS the page's existing call-back dialog
 * in the same click (JourneyCallbackContext.tsx): the same `CallbackForm`, the
 * same POST, the same lead and attribution. Never a second form, never two
 * dialogs stacked, never a navigation (that would drop the in-memory journey).
 * Once a call-back has been asked for on this journey the row shows the offer's
 * thank-you line instead. With no call-back on the screen — no provider, as in
 * the staff flow — there is no row and no hairline, only the text and Close.
 * Focus: the call-back dialog takes it on open (DialogShell); closed without a
 * send it comes back to the "Why we ask" link, after a send to the offer's
 * thank-you line.
 *
 * MOTION. The panel eases in (fade + a 6px rise, settling to `transform: none`)
 * only when `usePrefersReducedMotion` says motion is welcome; the global
 * reduced-motion kill in tokens.css is the second lock.
 *
 * NOT IN THE ANSWER RECORD. It is read-only copy; nothing here touches the
 * answer, and the staff fill-on-behalf form (pipeline/SinglePageForm.tsx) does
 * not show it — staff surfaces render unchanged in this batch.
 */
import { useEffect, useRef, useState } from 'react';
import { DialogShell } from '../ui/ConfirmDialog';
import { Button, InfoIcon } from '../ui/kit';
import { usePrefersReducedMotion } from '../ui/motion';
import { useJourneyCallback } from './JourneyCallbackContext';
import { CALLBACK_OFFER_ACTION, CALLBACK_OFFER_LEAD, CALLBACK_OFFER_THANKS } from './JourneyCallbackOffer';
import { JourneyButton } from './JourneyUi';
import { textParagraphs } from './paragraphs';

export const WHY_WE_ASK_LINK = 'Why we ask';
export const WHY_WE_ASK_TITLE = 'Why we ask this';

export function WhyWeAsk({ text }: { text: string | undefined }) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(false);
  // Set when this dialog closed to make way for the call-back dialog: focus is
  // that dialog's now, and pulling it back to the link would drop a keyboard
  // user behind the overlay.
  const handedOff = useRef(false);
  const callback = useJourneyCallback();
  const reducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (open) {
      wasOpen.current = true;
      return;
    }
    if (wasOpen.current) {
      wasOpen.current = false;
      if (handedOff.current) {
        handedOff.current = false;
        return;
      }
      buttonRef.current?.focus();
    }
  }, [open]);

  const paragraphs = textParagraphs(text);
  if (paragraphs.length === 0) return null;

  // ONE DIALOG AT A TIME. Both state changes land in the same React commit, so
  // this dialog is gone in the same paint the call-back dialog appears in — never
  // stacked, never a gap with no dialog. The link is where focus returns if the
  // patient closes the call-back without sending.
  function requestCallback() {
    if (!callback) return;
    handedOff.current = true;
    setOpen(false);
    callback.open(buttonRef.current);
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="gps-jux-why"
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
        // Block-level and only as wide as its words, so the hit area is the
        // link and not the whole row. The negative top margin collapses into
        // the help line's 18px bottom margin: the 44px box keeps its height
        // while the WORDS sit close under the text they explain.
        style={{
          display: 'flex',
          width: 'fit-content',
          alignItems: 'center',
          gap: '6px',
          minHeight: '44px',
          padding: '0 2px',
          margin: '-10px 0 8px',
          background: 'none',
          border: 0,
          color: 'var(--gps-link)',
          fontFamily: 'inherit',
          fontSize: '14.5px',
          fontWeight: 600,
          textDecoration: 'underline',
          textUnderlineOffset: '3px',
          cursor: 'pointer',
        }}
      >
        <InfoIcon size={16} />
        {WHY_WE_ASK_LINK}
      </button>
      {open ? (
        <DialogShell
          title={WHY_WE_ASK_TITLE}
          onCancel={() => setOpen(false)}
          width="min(34rem, 100%)"
          className={reducedMotion ? 'gps-jux-why-dialog' : 'gps-jux-why-dialog gps-jux-why-enter'}
          // Tab stays in the dialog: the answers come next in the DOM, and one
          // of them submits on a single key (see the header, ACCESSIBILITY).
          trapFocus
        >
          {/* Drawn beside the title by the panel's grid (tokens.css); after it in
              the DOM, so the heading is still the first thing read. */}
          <span className="gps-jux-why-badge" aria-hidden="true">
            <InfoIcon size={20} />
          </span>
          <div className="gps-jux-why-body">
            {paragraphs.map((paragraph, index) => (
              <p key={index} className="gps-jux-why-text">
                {paragraph}
              </p>
            ))}
            {callback ? <hr className="gps-jux-why-rule" /> : null}
            <div className="gps-jux-why-foot">
              {callback ? (
                <div className="gps-jux-why-callback">
                  {callback.requested ? (
                    <p className="gps-jux-why-thanks">{CALLBACK_OFFER_THANKS}</p>
                  ) : (
                    <>
                      <p className="gps-jux-why-callback-lead">{CALLBACK_OFFER_LEAD}</p>
                      <JourneyButton
                        type="button"
                        outline
                        aria-haspopup="dialog"
                        onClick={requestCallback}
                        style={{ width: 'auto' }}
                      >
                        {CALLBACK_OFFER_ACTION}
                      </JourneyButton>
                    </>
                  )}
                </div>
              ) : null}
              <Button
                type="button"
                variant="primary"
                className="gps-jux-why-close"
                onClick={() => setOpen(false)}
                style={{ width: 'auto', padding: '11px 28px' }}
              >
                Close
              </Button>
            </div>
          </div>
        </DialogShell>
      ) : null}
    </>
  );
}
